import {
  Column,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  InitialTableState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import {
  CSSProperties,
  Fragment,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import { useDebounce } from 'use-debounce';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useGetTestId } from '@/hooks';
import { cn } from '@/lib/utils';

import { Pagination } from './pagination';
import { SearchField } from './search-field';

export type PaginatedTableProps<
  DataType,
  SubRowDataType = unknown,
> = PropsWithChildren<{
  name?: string;
  columns: ColumnDef<DataType>[];
  subRowColumns?: ColumnDef<SubRowDataType>[];
  data: DataType[];
  totalCount: number;
  pagination: { pageIndex: number; pageSize: number };
  searchValue?: string;
  onPaginationChange?: (pagination: {
    pageIndex: number;
    pageSize: number;
  }) => void;
  onSearchChange?: (search: string) => void;
  searchable?: boolean;
  searchPlaceholder?: string;
  onClick?: (item: DataType) => void;
  onSubRowClick?: (item: SubRowDataType) => void;
  tableRowClassName?: string;
  tableCellClassName?: string;
  pinEnabled?: boolean;
  sizeStyleProperty?: 'width' | 'minWidth';
  emptyTitle?: string;
  emptyDescription?: string;
  emptyButton?: ReactNode;
  disabledColumnsIdsToClick?: string[];
  disabledSubColumnsIdsToClick?: string[];
  isLoading?: boolean;
  hasFilters?: boolean;
  initialState?: InitialTableState;
  onRowSelectionChange?: (selection: string[]) => void;
  getRowId?: (row: DataType) => string;
  tableFilterClassName?: string;
  emptyCellClassName?: string;
  subRowKey?: string;
  totalCountLabel?: string;
}>;

export function PaginatedTable<
  DataType extends { id: string; subrows?: any[] },
  SubRowDataType = unknown,
>({
  name = 'table',
  columns,
  data,
  totalCount,
  pagination,
  searchValue = '',
  onPaginationChange,
  onSearchChange,
  searchable = false,
  searchPlaceholder,
  children,
  onClick,
  onSubRowClick,
  tableRowClassName,
  tableCellClassName,
  pinEnabled = false,
  sizeStyleProperty = 'width',
  emptyTitle,
  emptyDescription = 'No results.',
  emptyButton,
  disabledColumnsIdsToClick = [],
  disabledSubColumnsIdsToClick = [],
  isLoading = false,
  hasFilters,
  initialState,
  tableFilterClassName,
  emptyCellClassName,
  onRowSelectionChange,
  getRowId,
  subRowKey = 'subrows',
  subRowColumns,
  totalCountLabel,
}: PaginatedTableProps<DataType, SubRowDataType>) {
  const testId = useGetTestId(name);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState({});
  const [expanded, setExpanded] = useState({});

  const [debouncedRowSelection] = useDebounce(rowSelection, 300);

  const table = useReactTable({
    data,
    columns,
    manualPagination: true,
    pageCount: Math.ceil(totalCount / pagination.pageSize),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    state: { sorting, pagination, rowSelection, expanded },
    onPaginationChange,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onExpandedChange: setExpanded, // ✅ update expanded rows
    initialState,
    columnResizeMode: 'onChange',
    getRowId: getRowId ? getRowId : undefined,
  });

  useEffect(() => {
    onRowSelectionChange?.(
      table.getSelectedRowModel().rows.map((row) => row.original.id)
    );
  }, [Object.keys(debouncedRowSelection).length]);

  const getCommonPinningStyles = (column: Column<DataType>): CSSProperties => {
    if (!pinEnabled) return {};

    const isPinned = column.getIsPinned();
    const isLastLeftPinnedColumn =
      isPinned === 'left' && column.getIsLastColumn('left');
    const isFirstRightPinnedColumn =
      isPinned === 'right' && column.getIsFirstColumn('right');

    return {
      boxShadow: isLastLeftPinnedColumn
        ? '-2px 0 2px -2px gray inset'
        : isFirstRightPinnedColumn
          ? '2px 0 2px -2px gray inset'
          : undefined,
      left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
      right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
      position: isPinned ? 'sticky' : 'relative',
      width: column.getSize(),
      zIndex: isPinned ? 1 : 0,
    };
  };

  return (
    <div className="w-full relative">
      {/* Search + filters */}
      <div
        className={cn(
          'flex justify-between items-center gap-4 mb-5',
          tableFilterClassName
        )}
      >
        {searchable && (
          <SearchField
            placeholder={searchPlaceholder}
            defaultValue={searchValue}
            className="w-full max-w-[400px]"
            onChange={onSearchChange}
          />
        )}
        {children}
      </div>
      {totalCount !== undefined && totalCountLabel && (
        <p className="text-sm text-zinc-400 my-2">{`All ${totalCountLabel}: ${totalCount}`}</p>
      )}

      <div className="relative">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="bg-zinc-900 border-0">
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="bg-zinc-900"
                    style={{
                      [sizeStyleProperty]: header.getSize(),
                      ...getCommonPinningStyles(header.column),
                    }}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {/* Loading skeleton rows */}
            {isLoading ? (
              Array.from({ length: 20 }).map((_, i) => (
                <TableRow key={`loading-${i}`}>
                  {columns.map((_, j) => (
                    <TableCell key={`cell-${i}-${j}`}>
                      <div className="animate-pulse bg-zinc-800 h-4 rounded w-3/4"></div>
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : data.length ? (
              table.getRowModel().rows.map((row, index) => (
                <Fragment key={`${row.id}-${index}`}>
                  <TableRow
                    data-testid={`${testId}-row-${index}`}
                    className={cn(
                      Boolean(onClick) && 'cursor-pointer',
                      tableRowClassName
                    )}
                    onClick={() => row.toggleExpanded()} // ✅ toggle subrow
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          'capitalize bg-zinc-950',
                          cell.column.id === 'status' && 'p-0',
                          tableCellClassName
                        )}
                        style={{ ...getCommonPinningStyles(cell.column) }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (
                            disabledColumnsIdsToClick.includes(cell.column.id)
                          ) {
                            return;
                          }
                          if (onClick) {
                            onClick?.(row.original);
                          }

                          if (subRowColumns) {
                            row.toggleExpanded();
                          }
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* ✅ Subrow */}
                  {row.getIsExpanded() && (
                    <TableRow className="bg-muted/50 hover:bg-muted/60">
                      <TableCell colSpan={columns.length}>
                        <div>
                          {Array.isArray(row.original[subRowKey]) &&
                          row.original[subRowKey].length > 0 ? (
                            (() => {
                              const subData = row.original[
                                subRowKey
                              ] as SubRowDataType[];

                              // ✅ Case 1: Configured subrow columns
                              if (subRowColumns) {
                                return (
                                  <Table>
                                    <TableHeader className="h-1">
                                      <TableRow>
                                        {subRowColumns.map((col) => (
                                          <TableHead
                                            key={
                                              //@ts-ignore
                                              col.id || String(col.accessorKey)
                                            }
                                            className="text-xs"
                                          >
                                            {typeof col.header === 'string'
                                              ? col.header
                                              : flexRender(
                                                  col.header,
                                                  {} as any
                                                )}
                                          </TableHead>
                                        ))}
                                      </TableRow>
                                    </TableHeader>

                                    <TableBody className="text-sm">
                                      {subData.map((sub, idx) => (
                                        <TableRow key={`sub-${idx}`}>
                                          {subRowColumns.map((col) => {
                                            const key =
                                              //@ts-ignore
                                              col.accessorKey as keyof SubRowDataType;
                                            const value = key
                                              ? sub[key]
                                              : undefined;

                                            return (
                                              <TableCell
                                                key={String(key)}
                                                className="py-1 text-sm"
                                                style={{ width: col?.size }}
                                                onClick={() => {
                                                  if (
                                                    disabledSubColumnsIdsToClick.includes(
                                                      //@ts-ignore
                                                      col.id || col.accessorKey
                                                    )
                                                  ) {
                                                    return;
                                                  }
                                                  onSubRowClick({
                                                    ...sub,
                                                    parent: row.original,
                                                  });
                                                }}
                                              >
                                                {col.cell
                                                  ? flexRender(col.cell, {
                                                      getValue: () => value,
                                                      row: {
                                                        original: {
                                                          ...sub,
                                                          parent: row.original,
                                                        },
                                                      },
                                                    } as any)
                                                  : String(value ?? '')}
                                              </TableCell>
                                            );
                                          })}
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                );
                              }

                              // ✅ Case 2: Fallback (no columns configured)
                              return (
                                <Table className="ml-4 border-zinc-800">
                                  <TableBody>
                                    {subData.map((sub, idx) => (
                                      <TableRow key={`sub-${idx}`}>
                                        {Object.entries(sub).map(
                                          ([key, value]) => (
                                            <TableCell key={key}>
                                              {String(value)}
                                            </TableCell>
                                          )
                                        )}
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              );
                            })()
                          ) : (
                            <div className="text-zinc-500 italic">
                              No additional details
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))
            ) : (
              <TableRow className={tableRowClassName} data-testid="empty-row">
                {hasFilters ? (
                  <TableCell
                    colSpan={columns.length}
                    className={cn(
                      'h-80 text-center text-zinc-500',
                      emptyCellClassName
                    )}
                  >
                    No results found
                  </TableCell>
                ) : (
                  <TableCell
                    colSpan={columns.length}
                    className={cn('h-40', emptyCellClassName)}
                  >
                    <div
                      className={cn(
                        'flex flex-col gap-2 justify-start items-start',
                        { 'items-center': !emptyTitle }
                      )}
                    >
                      <p className="text-md text-semibold">
                        {emptyTitle && emptyTitle}
                        <p
                          className={cn('text-zinc-500 text-xs', {
                            'text-md': !emptyTitle,
                          })}
                        >
                          {emptyDescription}
                        </p>
                      </p>
                      {emptyButton && emptyButton}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Optional overlay loader */}
        {isLoading && data.length > 0 && (
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center">
            <div className="text-zinc-200 text-sm animate-pulse">
              Loading...
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      <Pagination
        table={table}
        totalCount={totalCountLabel ? undefined : totalCount}
        className="-ml-[24px] -mr-[24px]"
      />
    </div>
  );
}
